import { MapPin, Clock, Phone, Mail, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/layout/Layout";
import rosconImage from "@/assets/roscon-reyes.jpg";
import bakeryImage from "@/assets/bakery-interior.jpg";

const Index = () => {
  const locations = [
    {
      zone: "Siete Palmas",
      name: "Av. Pintor Felo Monzón, 2",
      address: "35019 – Las Palmas de Gran Canaria",
      phone: "928 13 71 95",
    },
    {
      zone: "Lomo Los Frailes",
      name: "C/ Jordán, 42",
      address: "35018 – Las Palmas de Gran Canaria",
      phone: "928 66 59 67",
    },
  ];

  return (
    <Layout>
      {/* Novedades Section */}
      <section className="py-12 md:py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full mb-4">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-medium">Temporada Navideña</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-4">
              Novedades
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Descubre nuestras especialidades de temporada, elaboradas con la misma
              dedicación artesanal de siempre.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
            <div className="relative">
              <div className="absolute -inset-4 bg-secondary/20 rounded-lg -rotate-3"></div>
              <img
                src={rosconImage}
                alt="Roscón de Reyes tradicional con nata y frutas confitadas"
                className="relative rounded-lg shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
            <div className="space-y-6">
              <div>
                <span className="text-secondary font-semibold text-sm uppercase tracking-wider">
                  Producto Estrella
                </span>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mt-2">
                  Roscón de Reyes
                </h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Nuestro tradicional Roscón de Reyes, elaborado con masa brioche
                esponjosa, relleno de nata montada artesanal y coronado con frutas
                confitadas y almendras laminadas. ¡El sabor de la tradición!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/encargos">
                  <Button size="lg" className="w-full sm:w-auto">
                    Hacer Encargo
                  </Button>
                </Link>
                <Link to="/carta">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto">
                    Ver Carta Completa
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historia Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1 space-y-6">
              <span className="text-secondary font-semibold text-sm uppercase tracking-wider">
                Nuestra Historia
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary">
                Más de 45 Años de Tradición Artesanal
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Desde 1979, <strong className="text-primary">Pastelería Reyes</strong> ha
                  sido sinónimo de calidad y tradición en Gran Canaria. Lo que comenzó
                  como un pequeño obrador familiar se ha convertido en un referente de
                  la pastelería artesanal canaria.
                </p>
                <p>
                  Mantenemos las recetas tradicionales que nos han acompañado durante
                  generaciones, mientras nos adaptamos a los nuevos tiempos con
                  ingredientes de primera calidad y técnicas actualizadas.
                </p>
                <p>
                  Cada producto que sale de nuestro obrador lleva el sello de la
                  dedicación, el cariño y el saber hacer de más de cuatro décadas de
                  experiencia.
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-accent/20 rounded-lg rotate-2"></div>
                <img
                  src={bakeryImage}
                  alt="Interior tradicional de Pastelería Reyes con vitrinas de productos"
                  className="relative rounded-lg shadow-xl w-full object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ubicaciones Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">
              Visítanos
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              Nuestras Ubicaciones
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Encuéntranos en nuestros establecimientos en Las Palmas de Gran
              Canaria.
            </p>
          </div>

          <div className={`grid gap-6 max-w-5xl mx-auto ${locations.length === 1 ? 'md:grid-cols-1 max-w-md' : locations.length === 2 ? 'md:grid-cols-2 max-w-3xl' : 'md:grid-cols-3'}`}>
            {locations.map((location, index) => (
              <Card key={index} className="bg-background border-border hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <MapPin className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
                    <div>
                      <span className="text-xs font-semibold text-secondary uppercase tracking-wider">{location.zone}</span>
                      <h3 className="font-semibold text-primary">{location.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {location.address}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={`tel:${location.phone.replace(/\s/g, "")}`}
                      className="text-primary hover:text-secondary transition-colors font-medium"
                    >
                      {location.phone}
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Horario */}
          <Card className="max-w-2xl mx-auto mt-12 bg-background border-border">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="h-6 w-6 text-secondary" />
                <h3 className="font-serif text-2xl font-bold text-primary">
                  Horario de Apertura
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 text-center">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <p className="font-semibold text-primary">Lunes a Sábado</p>
                  <p className="text-xl font-bold text-secondary mt-1">9:00 - 14:00</p>
                  <p className="text-xl font-bold text-secondary">16:30 - 20:30</p>
                </div>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <p className="font-semibold text-primary">Domingos</p>
                  <p className="text-2xl font-bold text-secondary mt-1">9:00 - 14:00</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contacto */}
          <div className="max-w-2xl mx-auto mt-8 text-center">
            <h3 className="font-semibold text-primary mb-4">¿Tienes alguna pregunta?</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+34928137195"
                className="flex items-center gap-2 text-primary hover:text-secondary transition-colors"
              >
                <Phone className="h-5 w-5" />
                <span>928 13 71 95</span>
              </a>
              <span className="hidden sm:block text-border">|</span>
              <a
                href="mailto:pasteleriareyes@gmail.com"
                className="flex items-center gap-2 text-primary hover:text-secondary transition-colors"
              >
                <Mail className="h-5 w-5" />
                <span>pasteleriareyes@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
